import { createSlice } from '@reduxjs/toolkit';
import { validateModel, validateFormField } from '../validation/contactDetails';
import { canProceed } from '../utils/validation/canProceed';

const initialState = {
  validation: {},
  hasTriedToSave: false,
  isDirty: false,
  isSaving: false,
  isLoading: false,
  canContinue: false,
  model: {
    emailAddress: '',
    mobileNumber: '',
    homePhoneNumber: '',
    workPhoneNumber: '',
    preferredMethod: 'EMAIL'
  }
};

const contactDetails = createSlice({
  name: 'contactDetails',
  initialState,
  reducers: {
    loadContactDetails: (state) => {
      state.isLoading = true;
    },
    loadContactDetailsCompleted: (state, action) => {
      state.model = action.payload;
      state.isLoading = false;
    },
    loadContactDetailsRejected: (state) => {
      state.isLoading = false;
    },
    modelChange: (state, action) => {
      state.model = action.payload;
      state.isDirty = true;
    },
    fieldChange: (state, { payload: { field, value, section } }) => {
      if (section) {
        state.model[section][field] = value;
      } else {
        state.model[field] = value;
      }
      state.isDirty = true;
    },
    validate: (state, action) => {
      state.validation = validateModel(action.payload);
      state.canContinue = canProceed(state.validation);
    },
    validateForReview: (state, action) => {
      state.validation = validateModel(action.payload);
    },
    validateField: (state, action) => {
      const { field, value } = action.payload;

      state.validation[field] = validateFormField(field, value);
      state.canContinue = canProceed(state.validation);
    },
    resetValidation: (state) => {
      state.validation = initialState.validation;
      state.canContinue = false;
    },
    save: (state) => {
      state.isSaving = true;
      state.hasTriedToSave = true;
    },
    saveCompleted: (state) => {
      state.isSaving = false;
    },
    saveCancelled: (state) => {
      state.isSaving = false;
    },
    saveRejected: (state) => {
      state.isSaving = false;
    },
    resetState: () => initialState,
    updateContactDetails: (state, action) => {
      const { contact_details } = action.payload;
      state.model = {
        emailAddress: contact_details.email || state.model.emailAddress,
        mobileNumber: contact_details.phone.mobile || state.model.mobileNumber,
        homePhoneNumber: contact_details.phone.home || state.model.homePhoneNumber,
        workPhoneNumber: contact_details.phone.work || state.model.workPhoneNumber,
        preferredMethod: contact_details.preferences.contact.method || state.model.preferredMethod
      };
    }
  }
});

/**
 * Validates a given model, then if continuation is possible, will reset validation and
 * call the provided `callback` function.
 * @param {*} model
 * @param {Function} callback
 * @returns
 */
export function tryToContinue(model, callback) {
  return (dispatch) => {
    dispatch(validate(model));
    dispatch(checkCanContinue(callback));
  };
}

function checkCanContinue(callback) {
  return (dispatch, getState) => {
    const { canContinue } = getState().contactDetails;
    if (canContinue) {
      dispatch(resetValidation());
      if (typeof callback === 'function') {
        callback();
      }
    }
  };
}

export function confirmChanges(editState, callback) {
  return (dispatch) => {
    dispatch(validate(editState));
    dispatch(confirmModelChanges(editState, callback));
  };
}

function confirmModelChanges(editState, callback) {
  return (dispatch, getState) => {
    const { canContinue } = getState().contactDetails;
    if (canContinue) {
      dispatch(modelChange(editState));
      if (typeof callback === 'function') {
        callback();
      }
    }
  };
}

export { contactDetails };
export const {
  fieldChange,
  loadContactDetails,
  loadContactDetailsCompleted,
  loadContactDetailsRejected,
  updateContactDetails,
  modelChange,
  resetValidation,
  save,
  saveCancelled,
  saveCompleted,
  saveRejected,
  validate,
  validateForReview,
  validateField,
  resetState
} = contactDetails.actions;
